import React, { useEffect, useMemo, useState } from "react";
import { X, Lock, Unlock, Loader2, BedDouble, Minus, Plus } from "lucide-react";
import { toLocalISODate } from "../../utils/date";
import { bookingLabel } from "../../utils/bookingLabel";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const HOLDING = ["Confirmed", "Pending", "Completed", "Blocked"];
const dayOf = d => toLocalISODate(new Date(d));

function addDays(ymd, n) {
  const [y, m, d] = ymd.split("-").map(Number);
  return toLocalISODate(new Date(y, m - 1, d + n));
}
const pretty = ymd => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

/* Every room over one night — or a run of nights — and what can be done
   about each.

   Opened by tapping a day in the calendar's Inventory view. Blocking was
   reachable only through a dialog asking for rooms and a date range, or a
   drag in the list view that no touch screen can perform; the natural
   gesture — tap the day, tap the room — did nothing.

   Free rooms can be blocked (for this night or several); blocked rooms can
   have just this night reopened, or the whole block lifted; a guest's room
   shows who is in it and offers neither, because a block over a booking
   would hide the guest and say nothing true about the room. The server
   re-checks that (requireFree) rather than trusting this screen.

   Opened for several nights (a drag across days), or with the stepper
   raised, every room is judged over all of them: a room is free only if it
   is free every night, and Unblock reopens just those nights. The bookings
   are fetched here for exactly those nights, since a run can cross into a
   month the calendar behind this panel hasn't loaded. */
export default function DayInventorySheet({ date, initialNights = 1, authHeaders, onClose, onChanged }) {
  const [rooms, setRooms] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [nights, setNights] = useState(initialNights);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);      // key of the row doing something
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/rooms`, { headers: authHeaders() })
      .then(r => r.json()).then(j => setRooms((j.data || []).filter(r => r.active !== false)))
      .catch(() => setRooms([]));
  }, []);

  const until = addDays(date, nights);
  const lastNight = addDays(date, nights - 1);
  const span = nights > 1 ? `${pretty(date)} → ${pretty(lastNight)}` : pretty(date);

  const loadBookings = async () => {
    try {
      const j = await fetch(`${API}/calendar?from=${date}&to=${until}`, { headers: authHeaders() }).then(r => r.json());
      setBookings(j.data || []);
    } catch { setBookings([]); }
  };
  useEffect(() => { loadBookings(); }, [until]);

  // Everything holding a room on any of the chosen nights.
  const inRange = useMemo(() => (bookings || []).filter(b =>
    HOLDING.includes(b.status) && dayOf(b.checkIn) < until && dayOf(b.checkOut) > date), [bookings, date, until]);

  // How many of the chosen nights a stay covers.
  const nightsOf = b => {
    const a = dayOf(b.checkIn) > date ? dayOf(b.checkIn) : date;
    const z = dayOf(b.checkOut) < until ? dayOf(b.checkOut) : until;
    let n = 0;
    for (let d = a; d < z; d = addDays(d, 1)) n++;
    return n;
  };

  const rows = useMemo(() => (rooms || []).map(r => {
    const units = (r.units || []).filter(u => u.active !== false).map(u => {
      const holders = inRange.filter(b => String(b.unitId) === String(u._id));
      const guest = holders.find(b => b.status !== "Blocked");
      const blocks = holders.filter(b => b.status === "Blocked");
      const state = !holders.length ? "free" : guest ? "booked" : "blocked";
      const blockedNights = blocks.reduce((n, b) => n + nightsOf(b), 0);
      return { unit: u, holder: guest || blocks[0], blocks, state, blockedNights };
    });
    const unassigned = inRange.filter(b => String(b.room?._id || b.room) === String(r._id) && !b.unitId).length;
    return { room: r, units, unassigned };
  }), [rooms, inRange]);

  const freeCount  = rows.reduce((n, r) => n + r.units.filter(u => u.state === "free").length, 0);
  const totalCount = rows.reduce((n, r) => n + r.units.length, 0);

  async function run(key, fn) {
    setBusy(key); setError("");
    try { await fn(); await Promise.all([loadBookings(), onChanged()]); }
    catch (err) { setError(err.message || "That didn't work."); }
    finally { setBusy(null); }
  }
  const post = async (path, body) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify(body || {}),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || j.success === false) throw new Error(j.error || `Request failed (${res.status})`);
    return j;
  };

  const block = (room, unit) => post("/bookings/block", {
    roomId: room._id, unitId: unit._id, checkIn: date, checkOut: until, reason: reason.trim(), requireFree: true,
  });

  async function blockAllFree() {
    await run("all", async () => {
      const failed = [];
      for (const r of rows) for (const u of r.units.filter(x => x.state === "free")) {
        try { await block(r.room, u.unit); }
        catch (e) { failed.push(`${u.unit.roomNumber || r.room.name}: ${e.message}`); }
      }
      if (failed.length) throw new Error(`Some rooms were not blocked — ${failed.join(" · ")}`);
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm sm:p-4"
      role="dialog" aria-modal="true" aria-label={`Rooms on ${span}`} onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-2">
          <div>
            <h3 className="font-semibold text-gray-900">{span}</h3>
            <p className="text-xs text-gray-500">{rooms && bookings
              ? `${freeCount} of ${totalCount} rooms free ${nights > 1 ? `all ${nights} nights` : "tonight"}`
              : "Loading rooms…"}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="shrink-0 w-11 h-11 -mr-2 flex items-center justify-center rounded-xl hover:bg-gray-100">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* How long a new block lasts, and why. */}
        <div className="px-5 pb-3 grid grid-cols-[auto_1fr] gap-3 items-end border-b border-gray-100">
          <div>
            <span className="block text-[11px] font-semibold text-gray-500 mb-1">Nights</span>
            <div className="flex items-center rounded-xl border border-gray-200">
              <button type="button" onClick={() => setNights(n => Math.max(1, n - 1))} aria-label="Fewer nights"
                className="w-11 h-11 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-l-xl"><Minus size={15} /></button>
              <span className="min-w-[64px] text-center text-sm font-semibold text-gray-900 tabular-nums">{nights} night{nights > 1 ? "s" : ""}</span>
              <button type="button" onClick={() => setNights(n => Math.min(60, n + 1))} aria-label="More nights"
                className="w-11 h-11 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-r-xl"><Plus size={15} /></button>
            </div>
          </div>
          <label className="block min-w-0">
            <span className="block text-[11px] font-semibold text-gray-500 mb-1">Reason (optional)</span>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Maintenance, owner stay…"
              className="w-full min-h-[44px] px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </label>
          <p className="col-span-2 text-[11px] text-gray-400 -mt-1">
            A new block covers {pretty(date)}{nights > 1 ? ` to ${pretty(lastNight)}` : ""}; Unblock reopens the same nights.
          </p>
        </div>

        <div className="overflow-y-auto px-5 py-3 space-y-4 flex-1">
          {error && <p className="text-xs rounded-xl px-3 py-2.5 bg-rose-50 text-rose-700">{error}</p>}
          {(!rooms || !bookings) && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-amber-500" /></div>}

          {rows.map(({ room, units, unassigned }) => (
            <section key={room._id}>
              <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                <BedDouble size={14} className="text-gray-400" /> {room.name}
                <span className="font-normal text-gray-400">· {units.filter(u => u.state === "free").length} of {units.length} free</span>
              </h4>
              {unassigned > 0 && (
                <p className="text-[11px] rounded-lg px-3 py-2 mb-2 bg-amber-50 text-amber-800">
                  {unassigned} booking{unassigned > 1 ? "s" : ""} of this type have no room assigned and also use a room tonight.
                </p>
              )}
              <ul className="space-y-2">
                {units.map(({ unit, holder, blocks, state, blockedNights }) => {
                  const key = String(unit._id);
                  const multi = holder && dayOf(holder.checkOut) > addDays(dayOf(holder.checkIn), 1);
                  // One block, reaching past the chosen nights: it can also be lifted whole.
                  const wider = nights === 1 ? multi : blocks.length === 1 && (dayOf(holder.checkIn) < date || dayOf(holder.checkOut) > until);
                  return (
                    <li key={key} className={`rounded-xl border px-3 py-2.5 ${state === "blocked" ? "border-slate-200 bg-slate-50" : "border-gray-100"}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">Room {unit.roomNumber || "—"}</p>
                          <p className="text-[11px] text-gray-500 truncate">
                            {state === "free" && (nights > 1 ? `Free all ${nights} nights` : "Free tonight")}
                            {state === "booked" && `${bookingLabel(holder)} · ${holder.source || ""}`}
                            {state === "blocked" && nights > 1 && blockedNights < nights && `${blockedNights} of ${nights} nights · `}
                            {state === "blocked" && `Blocked${holder.iCalSummary && !/^blocked$/i.test(holder.iCalSummary) ? ` — ${holder.iCalSummary}` : ""} · ${pretty(dayOf(holder.checkIn))}${multi ? ` → ${pretty(addDays(dayOf(holder.checkOut), -1))}` : ""}`}
                          </p>
                        </div>
                        {state === "free" && (
                          <button onClick={() => run(key, () => block(room, unit))} disabled={!!busy}
                            className="shrink-0 inline-flex items-center gap-1.5 min-h-[44px] px-4 text-xs font-semibold rounded-xl text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50">
                            {busy === key ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Block
                          </button>
                        )}
                        {state === "booked" && (
                          <span className="shrink-0 text-[11px] font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">Booked</span>
                        )}
                      </div>
                      {state === "blocked" && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          <button onClick={() => run(key, () => nights > 1
                              ? Promise.all(blocks.map(b => post(`/bookings/${b._id}/unblock-night`, { date, endDate: lastNight })))
                              : post(`/bookings/${holder._id}/unblock-night`, { date }))} disabled={!!busy}
                            className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 text-xs font-semibold rounded-xl text-white
                              bg-gradient-to-r from-amber-500 to-orange-500 disabled:opacity-50">
                            {busy === key ? <Loader2 size={14} className="animate-spin" /> : <Unlock size={14} />}
                            {nights > 1 ? (blocks.length > 1 || wider ? "Unblock these nights" : "Unblock") : multi ? "Unblock this night" : "Unblock"}
                          </button>
                          {wider && (
                            <button onClick={() => {
                              if (!window.confirm(`Reopen every night of this block (${pretty(dayOf(holder.checkIn))} → ${pretty(addDays(dayOf(holder.checkOut), -1))})?`)) return;
                              run(key + ":all", () => post(`/bookings/${holder._id}/cancel`, {}));
                            }} disabled={!!busy}
                              className="inline-flex items-center gap-1.5 min-h-[44px] px-3.5 text-xs font-medium rounded-xl border border-gray-200 text-gray-700 hover:bg-white disabled:opacity-50">
                              {busy === key + ":all" && <Loader2 size={14} className="animate-spin" />} Unblock whole block
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          {rooms && !rows.length && <p className="text-sm text-gray-400 text-center py-6">No rooms set up yet.</p>}
        </div>

        {freeCount > 0 && (
          <div className="px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-gray-100">
            <button onClick={() => {
              if (!window.confirm(`Block all ${freeCount} free room${freeCount > 1 ? "s" : ""} for ${nights} night${nights > 1 ? "s" : ""} (${span})?`)) return;
              blockAllFree();
            }} disabled={!!busy}
              className="w-full inline-flex items-center justify-center gap-1.5 min-h-[48px] text-sm font-semibold rounded-xl border border-gray-200 text-gray-800 hover:bg-gray-50 disabled:opacity-50">
              {busy === "all" ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />}
              Block all {freeCount} free room{freeCount > 1 ? "s" : ""}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
